"use client";

import { useEffect, useState } from "react";
import { supabase } from "../supabase/client";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Image as ImageIcon, 
  X, 
  UploadCloud, 
  Layers,
  ExternalLink,
  Eye,
  EyeOff
} from "lucide-react";

export default function AdsManager() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({
    type: "slider",
    title: "",
    subtitle: "",
    cta: "",
    image_url: "",
    link: "",
    position: 0,
    is_active: true,
  });

  const fetchAds = async () => {
    const { data } = await supabase
      .from("ads")
      .select("*")
      .order("position", { ascending: true });
    setAds(data || []);
  };

  useEffect(() => {
    fetchAds();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${form.type}-${Date.now()}.${fileExt}`;

      const { error } = await supabase.storage
        .from("ad-images")
        .upload(fileName, file);

      if (error) throw error;

      const { data } = supabase.storage
        .from("ad-images")
        .getPublicUrl(fileName);

      setForm((prev) => ({ ...prev, image_url: data.publicUrl }));
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingId) {
        await supabase.from("ads").update(form).eq("id", editingId);
      } else {
        await supabase.from("ads").insert([form]);
      }
      resetForm();
      fetchAds();
    } catch (err) {
      alert("Error saving");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (ad) => {
    setForm(ad);
    setEditingId(ad.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this ad?")) return;
    await supabase.from("ads").delete().eq("id", id);
    fetchAds();
  };

  const toggleActive = async (ad) => {
    await supabase.from("ads").update({ is_active: !ad.is_active }).eq("id", ad.id);
    fetchAds();
  };

  const resetForm = () => {
    setForm({
      type: "slider",
      title: "",
      subtitle: "",
      cta: "",
      image_url: "",
      link: "",
      position: 0,
      is_active: true,
    });
    setEditingId(null);
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-10 font-sans text-slate-900">
      <div className="max-w-7xl mx-auto">
        
        {/* --- HEADER --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Ads Manager</h1>
            <p className="text-slate-500 mt-1">Manage banners, popups, and promotional content.</p>
          </div>
          <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-slate-200">
            <Layers size={18} className="text-indigo-600" />
            <span className="text-sm font-medium">{ads.length} Total Campaigns</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          
          {/* --- FORM SECTION --- */}
          <div className="lg:col-span-4">
            <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden sticky top-8">
              <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                <h2 className="font-bold text-lg flex items-center gap-2">
                  {editingId ? <Pencil size={18} /> : <Plus size={18} />}
                  {editingId ? "Modify Ad" : "New Campaign"}
                </h2>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                {/* Type Selection */}
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Display Type</label>
                  <select 
                    name="type" 
                    value={form.type} 
                    onChange={handleChange} 
                    className="w-full bg-slate-100 border-none rounded-xl p-3 text-sm focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                  >
                    <option value="hero">Hero Section</option>
                    <option value="slider">Main Slider</option>
                    <option value="popup">Entry Popup</option>
                    <option value="footer">Footer Bar</option>
                    <option value="promo_banner">Promo Banner</option>
                    <option value="app_section">App Section</option>
                  </select>
                </div>

                {/* Conditional Inputs */}
                <div className="space-y-4">
                  {form.type !== "footer" && (
                    <input 
                      name="title" 
                      placeholder="Headline Title" 
                      value={form.title || ""} 
                      onChange={handleChange} 
                      className="w-full border border-slate-200 rounded-xl p-3 text-sm focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  )}

                  {["hero", "slider"].includes(form.type) && (
                    <>
                      <input 
                        name="subtitle" 
                        placeholder="Sub-headline / Description" 
                        value={form.subtitle || ""} 
                        onChange={handleChange} 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm"
                      />
                      <input 
                        name="cta" 
                        placeholder="Button Text (e.g. Shop Now)" 
                        value={form.cta || ""} 
                        onChange={handleChange} 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm"
                      />
                    </>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <input 
                        name="link" 
                        placeholder="Destination URL (https://...)" 
                        value={form.link || ""} 
                        onChange={handleChange} 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-slate-400 uppercase ml-1">Sort Order</label>
                      <input 
                        type="number" 
                        name="position" 
                        value={form.position} 
                        onChange={handleChange} 
                        className="w-full border border-slate-200 rounded-xl p-3 text-sm"
                      />
                    </div>
                    <div className="flex items-end pb-3">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className={`w-10 h-5 rounded-full transition-colors relative ${form.is_active ? 'bg-indigo-600' : 'bg-slate-300'}`}>
                          <input 
                            type="checkbox" 
                            name="is_active" 
                            hidden 
                            checked={form.is_active} 
                            onChange={handleChange} 
                          />
                          <div className={`absolute top-1 w-3 h-3 bg-white rounded-full transition-all ${form.is_active ? 'left-6' : 'left-1'}`} />
                        </div>
                        <span className="text-sm font-medium text-slate-600 uppercase text-[10px] tracking-widest">Live</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Media Upload */}
                <div className="pt-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block text-center">Creative Asset</label>
                  {!form.image_url ? (
                    <label className="border-2 border-dashed border-slate-200 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors">
                      <div className="p-3 bg-indigo-50 rounded-full text-indigo-600">
                        <UploadCloud size={24} />
                      </div>
                      <span className="text-xs text-slate-500 font-medium">Click to upload image</span>
                      <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                    </label>
                  ) : (
                    <div className="relative group overflow-hidden rounded-2xl border border-slate-200">
                      <img src={form.image_url} className="w-full h-40 object-cover" alt="Preview" />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <button 
                          type="button" 
                          onClick={() => setForm({ ...form, image_url: "" })}
                          className="bg-white/20 backdrop-blur-md text-white p-2 rounded-full hover:bg-red-500 transition-colors"
                        >
                          <X size={20} />
                        </button>
                      </div>
                    </div>
                  )}
                  {uploading && <div className="mt-2 text-center text-xs animate-pulse text-indigo-600">Syncing to cloud...</div>}
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    disabled={loading || uploading}
                    className="flex-1 bg-slate-900 text-white font-bold py-3 rounded-xl hover:bg-indigo-600 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  >
                    {loading ? "..." : editingId ? "Update Campaign" : "Launch Ad"}
                  </button>
                  {editingId && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-4 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* --- LIST SECTION --- */}
          <div className="lg:col-span-8">
            <div className="grid md:grid-cols-2 gap-6">
              {ads.map((ad) => (
                <div key={ad.id} className="group bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md transition-shadow relative">
                  
                  {/* Status Badge Over Image */}
                  <div className="absolute top-3 left-3 z-10">
                    <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-tighter backdrop-blur-md border ${
                      ad.is_active 
                      ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-600" 
                      : "bg-slate-500/10 border-slate-500/20 text-slate-600"
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${ad.is_active ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                      {ad.is_active ? "Active" : "Paused"}
                    </div>
                  </div>

                  {/* Image Container */}
                  <div className="h-44 bg-slate-100 relative">
                    <img src={ad.image_url} alt="Curve&Fit" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute top-3 right-3 flex gap-2">
                      <button 
                        onClick={() => handleEdit(ad)}
                        className="p-2 bg-white/90 backdrop-blur shadow-sm rounded-lg text-slate-700 hover:text-indigo-600 transition-colors"
                      >
                        <Pencil size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">{ad.type.replace("_", " ")}</span>
                        <h3 className="font-bold text-slate-900 leading-tight truncate">{ad.title || "No Title"}</h3>
                      </div>
                      <span className="text-xs font-medium text-slate-400 bg-slate-50 px-2 py-0.5 rounded border border-slate-100">#{ad.position}</span>
                    </div>
                    
                    <p className="text-xs text-slate-500 line-clamp-2 min-h-[2rem] mb-4">
                      {ad.subtitle || "No description provided."}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-50">
                      <div className="flex gap-4">
                        <button 
                          onClick={() => toggleActive(ad)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-indigo-600 transition-colors"
                        >
                          {ad.is_active ? <EyeOff size={14}/> : <Eye size={14}/>}
                          {ad.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button 
                          onClick={() => handleDelete(ad.id)}
                          className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                      
                      {ad.link && (
                        <a 
                          href={ad.link} 
                          target="_blank" 
                          rel="noreferrer" 
                          className="p-1.5 text-slate-400 hover:text-indigo-600 transition-colors"
                        >
                          <ExternalLink size={16} />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              
              {ads.length === 0 && (
                <div className="col-span-full py-20 text-center bg-white rounded-3xl border-2 border-dashed border-slate-200">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4 text-slate-300">
                    <ImageIcon size={32} />
                  </div>
                  <h3 className="text-slate-900 font-bold">No ads found</h3>
                  <p className="text-slate-500 text-sm">Create your first campaign using the form on the left.</p>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}